import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Router, Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { IntlProvider, FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { get, isEmpty, isEqual } from 'lodash';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import history from '../helpers/history';
import asyncRoute from '../helpers/hoc/asyncRoute';
import bookingRoutes from '../routes/booking';
import { dispatchAuthTokenAction, dispatchLogoutAction } from '../actions/authentication';
import {
  fetchAppStartupInfoAction,
  clearApiErrorResponseAction,
  dispatchContextualHome,
  dispatchStartupInfoAndResourceDataAction,
  dispatchGuestLocationAction,
} from '../actions/common';
import { dispatchCallbackUrlAction } from '../actions/booking';

import { fetchUserDetailsAction, getUserAddressesAction } from '../actions/app.action';

import { fetchGoogleMapsApi } from '../api/api';
import {
  matchRoutePatternWithWildcardSupport,
  extractWhitelabelSubdomain,
  isAndroidWebView,
  isIosWebView,
  getDeviceType,
  compareVersions,
  getComponentVersionProps,
  isIosWebViewWrapper,
  isNativeWebView,
  setIsApiRequestOnHold,
  fetchGeoLocation,
  checkDistanceBetweenCoordinates,
  formatAddress,
  hasValidCoordinates,
} from '../utils/helpers';

import { getItem, removeItem, setItem } from '../utils/storage';
import {
  storageKeys,
  storageTypes,
  apiResponseCodes,
  transitionType,
  serverErrorCodes,
  toastMessageTime,
} from '../constants/globals';
import {
  getErrorTitle,
  getErrorDescription,
  getLocalityAddressFromAddressComponent,
} from '../selectors';
import { fetchAppResourcesAction } from '../actions/common';
import { navigationalPanelRoutes, nativeEventType } from '../config';
import { extractDataFromNative, handleLinkUrl } from '../helpers';
import { useDidUpdateEffect, useTheme } from '../utils/customHooks';
import { RewardsGreeting } from '../components/SplashScreen/RewardsGreeting';
import resourcesConfig from '../resources';
import ThemeContext from './ThemeContext';
import DefaultRegularFont from '../assets/fonts/OpenSans-Regular.ttf';
import DefaultSemiBoldFont from '../assets/fonts/OpenSans-SemiBold.ttf';
import DefaultBoldFont from '../assets/fonts/OpenSans-Bold.ttf';
import LocationPickerAnimation from '../components/LocationPickerAnimation';

import './index.scss';

const isLegacyCurrentLocationAddress = (address) => {
  const addressTitle = String(
    get(address, 'title') ||
      get(address, 'addressType') ||
      get(address, 'addressName') ||
      get(address, 'name') ||
      '',
  )
    .trim()
    .toLowerCase();
  return (
    get(address, 'currentLocation', false) ||
    addressTitle === String(resourcesConfig.currentLocation || '').trim().toLowerCase()
  );
};

const deviceType = getDeviceType();

// rewards container import
const Authentication = asyncRoute(() => import('./authentication'));
const Home = asyncRoute(() => import('./Home'));
const Account = asyncRoute(() => import('./Account'));
const Locations = asyncRoute(() => import('./Locations'));
const GetApp = asyncRoute(() => import('./GetApp'));
const RewardList = asyncRoute(() => import('./RewardList'));
const CouponsList = asyncRoute(() => import('./CouponsList'));
const RewardProgramDetails = asyncRoute(() => import('./RewardProgramDetails'));
const Referral = asyncRoute(() => import('./Referral'));
const AboutUs = asyncRoute(() => import('./AboutUs'));
const DeleteAccountSuccess = asyncRoute(() => import('./DeleteAccountSuccess'));
const LocationDetails = asyncRoute(() => import('./LocationDetails'));
const HelpAndSupport = asyncRoute(() => import('./HelpAndSupport'));
const VIPPassList = asyncRoute(() => import('./VIPPassList'));
const VIPPassDetails = asyncRoute(() => import('./VIPPassDetails'));
const VIPMembershipList = asyncRoute(() => import('./VIPMembershipList'));
const Payment = asyncRoute(() => import('./Payment'));
const SelectOutlet = asyncRoute(() => import('./SelectOutlet'));
const Location = asyncRoute(() => import('./Location'));
const Bookings = asyncRoute(() => import('./Bookings'));
const BookService = asyncRoute(() => import('./BookService'));
const Cart = asyncRoute(() => import('./Cart'));
const Confirmation = asyncRoute(() => import('./Confirmation'));
const Delivery = asyncRoute(() => import('./Delivery'));
const Policies = asyncRoute(() => import('./Policies'));
const ServiceCatalog = asyncRoute(() => import('./ServiceCatalog'));
const CategoryCatalog = asyncRoute(() => import('./CategoryCatalog'));
const BookingFeedback = asyncRoute(() => import('./BookingFeedback'));

// rewards components import

const NavigationPanel = asyncRoute(() => import('../components/NavigationPanel'));
const ExceptionComponent = asyncRoute(() => import('../components/ExceptionComponent'));
const NonExistentSubdomainFallback = asyncRoute(() =>
  import('./Fallback/NonExistentSubdomainFallback'),
);
const SessionExpiredPopover = asyncRoute(() => import('../components/SessionExpiredPopover'));
const DeliveryAddressPopup = asyncRoute(() => import('../components/Popup/DeliveryAddress'));

const isRewardsGreeterRendered = getItem(storageTypes.sessionStorage, 'isRewardsGreeterRendered');

const isLocationAnimationRendered = getItem(
  storageTypes.sessionStorage,
  'isLocationAnimationRendered',
);

const App = ({
  initialLocation,
  errorResponse,
  actions,
  resources,
  themeData,
  contextualHome,
  fontFamilyUrl,
  boldFontURL,
  semiBoldFontURL,
  userDetails,
  authData,
  userAddresses,
  selectedServiceAddress,
  appStartupInfo,
  isWebApplicable,
  hasTiers,
  defaultWebRedirectionUrl,
  ...props
}) => {
  const [activeTabContext, setActiveTabContext] = useState(bookingRoutes.containers.home);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorContent, setErrorContent] = useState({});
  const [appResources, setAppResources] = useState(resources);
  const [showShareAppFeature, setShowShareAppFeature] = useState(true);
  const [startupInteractionDone, setStartupInteractionDone] = useState(false);
  const [nativeRouteName, setNativeRouteName] = useState('');
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [location, setLocation] = useState(initialLocation);

  const [greetingSplashActive, setGreetingSplashActive] = useState(
    isRewardsGreeterRendered ? false : true,
  );
  const [showNonExistentSubdomainFallback, setShowNonExistentSubdomainFallback] = useState(false);
  const [showLocationAnimation, setShowLocationAnimation] = useState(
    isNativeWebView ? false : isLocationAnimationRendered ? false : true,
  );
  const [guestLocation, setGuestLocation] = useState({});
  const [locationPermissionStatus, setLocationPermissionStatus] = useState('');
  const [fetchingLocation, setFetchingLocation] = useState(true);
  const [fetchingLocality, setFetchingLocality] = useState(false);
  const [localityDetails, setLocalityDetails] = useState({});
  const [addressFetching, setAddressFetching] = useState(true);
  const [isInitialLocationFetched, setIsInitialLocationFetched] = useState(false);
  const [showDeliveryAddressPopup, setShowDeliveryAddressPopup] = useState(false);
  const [findingNearbyCompanion, setFindingNearbyCompanion] = useState(false);
  const [autoSelectedAddressToast, setAutoSelectedAddressToast] = useState('');
  const [autoSelectedAddressToastSequence, setAutoSelectedAddressToastSequence] = useState(0);
  const [locationPermissionPromptDismissed, setLocationPermissionPromptDismissed] = useState(false);
  const [suppressHomeLocationPermissionCard, setSuppressHomeLocationPermissionCard] = useState(() =>
    Boolean(getItem(storageTypes.sessionStorage, storageKeys.signupLocationFlowCompleted)),
  );
  const pathname = get(location, 'pathname', '');
  const showGreeterScreen = get(appResources, 'showGreeterScreen', true);
  const pagesExistInNative = getItem(storageTypes.localStorage, 'pagesInNative') || [];
  const webPathSegments = String(pathname).split(/[/?#]/).filter(Boolean);
  const isAuthenticationRoute =
    webPathSegments.includes(bookingRoutes.containers.authentication) &&
    !webPathSegments.includes(bookingRoutes.authentication.signUp);
  // Auth screens retain the native Android safe-area inset. Application Web
  // routes draw edge-to-edge and use the shared in-app header spacing.
  const useNativeWebInset =
    isNativeWebView &&
    !isAndroidWebView &&
    Boolean(pathname) &&
    !isAuthenticationRoute &&
    (!showGreeterScreen || !greetingSplashActive || !isEmpty(pagesExistInNative));
  const isThemeApplied = useTheme('rewards-container', themeData);
  const callbackUrlRef = useRef();
  const sessionExpiryLogoutTimerRef = useRef();
  const autoSelectedAddressToastTimerRef = useRef();
  const autoSelectedAddressIdRef = useRef('');
  const notifiedSavedAddressSelectionRef = useRef('');
  const selectedServiceAddressRef = useRef(selectedServiceAddress);
  const previousLocationPermissionStatusRef = useRef(locationPermissionStatus);
  const accessToken = get(authData, 'accessToken', '');

  selectedServiceAddressRef.current = selectedServiceAddress;

  const authenticationExemptedRoutes = isNativeWebView
    ? [bookingRoutes.containers.getApp, bookingRoutes.containers.authentication]
    : [
        bookingRoutes.containers.getApp,
        bookingRoutes.containers.scan,
        bookingRoutes.containers.authentication,
      ];

  const getActiveContainerRoute = (url = '') => {
    const pathname = String(url).split('?')[0];
    const pathSegments = pathname.split('/').filter(Boolean);
    const containerRoutes = Object.values(bookingRoutes.containers);

    return pathSegments.find((pathSegment) => containerRoutes.includes(pathSegment)) || '';
  };

  const normalizedPermissionPath = String(pathname).split('?')[0].replace(/\/$/, '');
  const permissionSearch = new URLSearchParams(String(get(location, 'search', '') || ''));
  const isSignupDeliveryPermissionRoute =
    [
      `/${bookingRoutes.containers.delivery}`,
      `/${bookingRoutes.booking}/${bookingRoutes.containers.delivery}`,
    ].includes(normalizedPermissionPath) &&
    permissionSearch.get('source') === bookingRoutes.authentication.signUp;
  const isDeliveryAddressPermissionRoute = [
    `/${bookingRoutes.containers.delivery}`,
    `/${bookingRoutes.booking}/${bookingRoutes.containers.delivery}`,
  ].includes(normalizedPermissionPath);
  const isHomeRoute = [
    `/${bookingRoutes.containers.home}`,
    `/${bookingRoutes.booking}/${bookingRoutes.containers.home}`,
  ].includes(normalizedPermissionPath);
  const isAuthenticatedHomePermissionRoute =
    [
      `/${bookingRoutes.containers.home}`,
      `/${bookingRoutes.booking}/${bookingRoutes.containers.home}`,
    ].includes(normalizedPermissionPath) &&
    (!isEmpty(userDetails) || !isEmpty(get(authData, 'accessToken', '')));
  const isLocationPermissionRoute =
    isDeliveryAddressPermissionRoute || isAuthenticatedHomePermissionRoute;
  const locationAccessUnavailable = [
    'denied',
    'services-disabled',
    'unavailable',
    'undetermined',
  ].includes(locationPermissionStatus);
  const signupLocationFlowCompleted = Boolean(
    getItem(storageTypes.sessionStorage, storageKeys.signupLocationFlowCompleted),
  );
  // During signup, keep the delivery-address map available for manual
  // selection after a native permission denial. The denial card is intended
  // for Home and regular Add New Address flows.
  const shouldShowLocationPermissionCard =
    !(isHomeRoute && (suppressHomeLocationPermissionCard || signupLocationFlowCompleted)) &&
    (!isSignupDeliveryPermissionRoute || !locationAccessUnavailable);

  useEffect(() => {
    if (isHomeRoute) {
      setSuppressHomeLocationPermissionCard(signupLocationFlowCompleted);
      if (signupLocationFlowCompleted) {
        removeItem(storageTypes.sessionStorage, storageKeys.signupLocationFlowCompleted);
      }
    } else {
      setSuppressHomeLocationPermissionCard(false);
    }
  }, [isHomeRoute, pathname]);

  useEffect(() => {
    if (!isNativeWebView) {
      if (!isWebApplicable) {
        setGreetingSplashActive(false);
      }
    }
  }, [isWebApplicable]);

  useEffect(() => {
    const handleFetchGeoLocationSuccess = (position) => {
      const draftLocationCoordinates = {
        lat: get(position, 'coords.latitude'),
        lng: get(position, 'coords.longitude'),
      };
      if (!hasValidCoordinates(draftLocationCoordinates.lat, draftLocationCoordinates.lng)) {
        setFetchingLocation(false);
        return;
      }
      setItem(storageTypes.localStorage, 'geoLocation', draftLocationCoordinates);
      setGuestLocation(draftLocationCoordinates);
    };
    if (isLocationPermissionRoute && (isNativeWebView || !isEmpty(userDetails))) {
      if (isNativeWebView) {
        const locationData = {
          permissionType: { type: nativeEventType.location },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(locationData));
      } else {
        fetchGeoLocation(handleFetchGeoLocationSuccess);
      }
    }
  }, [userDetails, pathname, isNativeWebView, isLocationPermissionRoute]);

  useEffect(() => {
    actions.fetchAppResources();
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(sessionExpiryLogoutTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (isEmpty(accessToken)) return;

    window.clearTimeout(sessionExpiryLogoutTimerRef.current);
    sessionExpiryLogoutTimerRef.current = undefined;
    setShowSessionExpired(false);
    setIsApiRequestOnHold(false);

    if (isEqual(get(errorResponse, 'code'), serverErrorCodes.invalidToken)) {
      actions.clearApiErrorResponse();
    }
  }, [accessToken]);

  useEffect(() => {
    handleFetchLocationPermissionStatus();
  }, [guestLocation]);

  useEffect(() => {
    const previousPermissionStatus = previousLocationPermissionStatusRef.current;
    const locationAccessWasUnavailable = [
      'denied',
      'services-disabled',
      'unavailable',
      'undetermined',
    ].includes(previousPermissionStatus);

    if (isEqual(locationPermissionStatus, 'granted')) {
      setLocationPermissionPromptDismissed(false);

      if (locationAccessWasUnavailable) {
        setIsInitialLocationFetched(false);
        setLocalityDetails({});
        setFetchingLocation(true);
        setShowLocationAnimation(true);
      }
    }

    previousLocationPermissionStatusRef.current = locationPermissionStatus;
  }, [locationPermissionStatus]);

  const handleFetchLocationPermissionStatus = () => {
    if (!isNativeWebView) {
      if (navigator.permissions) {
        navigator.permissions
          .query({ name: 'geolocation' })
          .then((result) => {
            console.log('status', { result });
            setLocationPermissionStatus(result.state);
          })
          .catch((error) => {
            console.log('status', { error });

            setLocationPermissionStatus('denied');
          });
      }
    } else {
      // for Native we'll get the permission status along with the location permission event
    }
  };

  // Fetch locality details when location coordinates are available
  useEffect(() => {
    const selectedLegacyCurrentLocation =
      get(selectedServiceAddress, 'savedAddress', false) &&
      isLegacyCurrentLocationAddress(selectedServiceAddress);
    if (selectedLegacyCurrentLocation) {
      actions.dispatchGuestLocation({ guestLocation: {} });
      setLocalityDetails({});
      setIsInitialLocationFetched(false);
    }

    if (
      !selectedLegacyCurrentLocation &&
      (get(selectedServiceAddress, 'id') || get(selectedServiceAddress, 'currentLocation')) &&
      hasValidCoordinates(
        get(selectedServiceAddress, 'latitude'),
        get(selectedServiceAddress, 'longitude'),
      )
    ) {
      setIsInitialLocationFetched(true);
      setFetchingLocation(false);
      setLocalityDetails(selectedServiceAddress);
      return;
    }

    if (
      hasValidCoordinates(get(guestLocation, 'lat'), get(guestLocation, 'lng')) &&
      !fetchingLocality &&
      isEmpty(localityDetails) &&
      !addressFetching &&
      !isInitialLocationFetched
    ) {
      setIsInitialLocationFetched(true);
      if (isEmpty(userAddresses)) {
        fetchLocalityDetails(guestLocation.lat, guestLocation.lng);
      } else {
        const { nearestAddress } = findNearestSavedAddress({
          guestLocation,
          userAddresses,
          resources,
        });
        if (!isEmpty(nearestAddress)) {
          autoSelectedAddressIdRef.current = get(nearestAddress, 'id', '');
          const autoSelectedAddress = { ...nearestAddress, selectionToken: Date.now() };
          actions.dispatchGuestLocation({
            // Service availability and distance pricing must use the selected
            // saved address coordinates, never the device coordinates.
            guestLocation: autoSelectedAddress,
          });
          setFetchingLocation(false);
          setLocalityDetails(autoSelectedAddress);
        } else {
          // if no nearest address found from saved addresses, fetch locality details
          fetchLocalityDetails(guestLocation.lat, guestLocation.lng);
        }
      }
    }
  }, [
    guestLocation,
    locationPermissionStatus,
    addressFetching,
    userAddresses,
    selectedServiceAddress,
  ]);

  // Address Book edits replace the selected address snapshot with the latest
  // saved record. This keeps distance pricing and the distance label in sync.
  useEffect(() => {
    const selectedAddressId = get(selectedServiceAddress, 'id', '');
    if (
      !selectedAddressId ||
      !get(selectedServiceAddress, 'savedAddress', false) ||
      addressFetching
    ) return;

    if (isLegacyCurrentLocationAddress(selectedServiceAddress)) {
      actions.dispatchGuestLocation({ guestLocation: {} });
      return;
    }

    const latestSavedAddress = userAddresses.find(
      (address) => get(address, 'id', '') === selectedAddressId,
    );
    if (isEmpty(latestSavedAddress)) {
      // The selected saved address was deleted. Remove its stale snapshot so
      // the header, catalogue, cart pricing and distance context cannot reuse it.
      window.clearTimeout(autoSelectedAddressToastTimerRef.current);
      autoSelectedAddressIdRef.current = '';
      setAutoSelectedAddressToast('');
      setLocalityDetails({});
      setIsInitialLocationFetched(false);
      actions.dispatchGuestLocation({ guestLocation: {} });
      return;
    }

    const { latitude, longitude, title, line1, locality, city, state, id } = latestSavedAddress;
    const refreshedAddress = {
      latitude,
      longitude,
      addressName: title,
      address: formatAddress({ line1, locality, city, state }),
      id,
      savedAddress: true,
      selectionToken: get(selectedServiceAddress, 'selectionToken'),
    };
    const selectedSnapshot = {
      latitude: get(selectedServiceAddress, 'latitude'),
      longitude: get(selectedServiceAddress, 'longitude'),
      addressName: get(selectedServiceAddress, 'addressName'),
      address: get(selectedServiceAddress, 'address'),
      id: selectedAddressId,
      savedAddress: true,
      selectionToken: get(selectedServiceAddress, 'selectionToken'),
    };

    if (!isEqual(refreshedAddress, selectedSnapshot)) {
      actions.dispatchGuestLocation({ guestLocation: refreshedAddress });
    }

    if (autoSelectedAddressIdRef.current !== selectedAddressId) {
      window.clearTimeout(autoSelectedAddressToastTimerRef.current);
      setAutoSelectedAddressToast('');
    }
  }, [selectedServiceAddress, userAddresses, addressFetching]);

  const findNearestSavedAddress = ({ guestLocation, userAddresses, resources }) => {
    if (
      hasValidCoordinates(get(guestLocation, 'lat'), get(guestLocation, 'lng')) &&
      !isEmpty(userAddresses)
    ) {
      let nearestAddress = null;
      let minDistance = Infinity;
      const configuredRange = Number(get(resources, 'savedAddressAutoSelectRadiusMeters'));
      const fallbackRange = Number(resourcesConfig.savedAddressAutoSelectRadiusMeters) || 200;
      // Remote resources can explicitly contain null/blank values. Do not let
      // those turn the proximity radius into NaN and silently disable address
      // auto-selection on a cold app launch.
      const requiredRange =
        Number.isFinite(configuredRange) && configuredRange > 0
          ? configuredRange
          : fallbackRange;
      let addressObj = {};
      userAddresses.forEach((address) => {
        if (isLegacyCurrentLocationAddress(address)) return;
        if (!hasValidCoordinates(address.latitude, address.longitude)) return;
        const distance = checkDistanceBetweenCoordinates(
          {
            latitude1: guestLocation.lat,
            longitude1: guestLocation.lng,
            latitude2: address.latitude,
            longitude2: address.longitude,
          },
          false, // This ensures no rounding off
        );

        const distanceInMeter = distance * 1000;

        if (distanceInMeter <= requiredRange && distanceInMeter < minDistance) {
          minDistance = distanceInMeter;
          nearestAddress = address;
        }

        // You can now add any logic to filter or select based on the distance here
      });
      if (nearestAddress) {
        const { latitude, longitude, title, id, locality, line1, city, state } = nearestAddress;
        addressObj = {
          latitude,
          longitude,
          addressName: title,
          address: formatAddress({ line1, locality, city, state }),
          id,
          savedAddress: true,
        };
      } else {
        console.log('No saved address found within the required range.');
      }
      return { nearestAddress: addressObj, distanceInMeters: minDistance };
    }
  };

  // Saved addresses can arrive after the initial device reverse-geocode has
  // already completed. Re-evaluate once address hydration finishes so a nearby
  // saved address always wins over an ID-less catalogue location.
  useEffect(() => {
    if (
      addressFetching ||
      isEmpty(userAddresses) ||
      get(selectedServiceAddress, 'id') ||
      get(selectedServiceAddress, 'currentLocation', false) ||
      !hasValidCoordinates(get(guestLocation, 'lat'), get(guestLocation, 'lng'))
    ) {
      return;
    }

    const { nearestAddress } =
      findNearestSavedAddress({ guestLocation, userAddresses, resources }) || {};
    if (isEmpty(nearestAddress)) return;

    const autoSelectedAddress = { ...nearestAddress, selectionToken: Date.now() };
    autoSelectedAddressIdRef.current = get(autoSelectedAddress, 'id', '');
    setLocalityDetails(autoSelectedAddress);
    setIsInitialLocationFetched(true);
    setFetchingLocation(false);
    actions.dispatchGuestLocation({ guestLocation: autoSelectedAddress });
  }, [
    addressFetching,
    userAddresses,
    get(guestLocation, 'lat'),
    get(guestLocation, 'lng'),
    get(selectedServiceAddress, 'id'),
    get(selectedServiceAddress, 'currentLocation'),
  ]);

  useEffect(
    () => () => window.clearTimeout(autoSelectedAddressToastTimerRef.current),
    [],
  );

  useEffect(() => {
    const addressId = get(selectedServiceAddress, 'id', '');
    if (!addressId || !get(selectedServiceAddress, 'savedAddress', false)) {
      window.clearTimeout(autoSelectedAddressToastTimerRef.current);
      notifiedSavedAddressSelectionRef.current = '';
      setAutoSelectedAddressToast('');
      return;
    }

    const selectionKey = `${addressId}:${get(selectedServiceAddress, 'selectionToken', 'initial')}`;
    if (notifiedSavedAddressSelectionRef.current === selectionKey) return;
    notifiedSavedAddressSelectionRef.current = selectionKey;

    const addressLabel = get(
      selectedServiceAddress,
      'addressName',
      resourcesConfig.savedAddress,
    );
    let message = get(
      resources,
      'savedAddressSelectedToast',
      resourcesConfig.savedAddressSelectedToast,
    ).replace('{addressName}', addressLabel);

    const deviceLatitude = get(guestLocation, 'lat');
    const deviceLongitude = get(guestLocation, 'lng');
    const addressLatitude = get(selectedServiceAddress, 'latitude');
    const addressLongitude = get(selectedServiceAddress, 'longitude');
    if (
      hasValidCoordinates(deviceLatitude, deviceLongitude) &&
      hasValidCoordinates(addressLatitude, addressLongitude)
    ) {
      const distanceInKm = checkDistanceBetweenCoordinates(
        {
          latitude1: deviceLatitude,
          longitude1: deviceLongitude,
          latitude2: addressLatitude,
          longitude2: addressLongitude,
        },
        false,
      );
      const useKilometres = distanceInKm >= 1;
      const distance = useKilometres ? distanceInKm.toFixed(1) : Math.round(distanceInKm * 1000);
      const unit = useKilometres ? 'km' : 'm';
      message = get(
        resources,
        'autoSelectedServiceAddressToast',
        resourcesConfig.autoSelectedServiceAddressToast,
      )
        .replace('{addressName}', addressLabel)
        .replace('{distance}', distance)
        .replace('{unit}', unit);
    }

    setAutoSelectedAddressToast(message);
    setAutoSelectedAddressToastSequence((sequence) => sequence + 1);
  }, [selectedServiceAddress, guestLocation, resources]);

  const canShowAutoSelectedAddressToast =
    isThemeApplied && isHomeRoute && !greetingSplashActive && !isEmpty(autoSelectedAddressToast);

  // Start the dismissal timer only when Home is actually visible. Previously
  // the timer could expire behind the greeting splash while the app was loading.
  useEffect(() => {
    if (!canShowAutoSelectedAddressToast) return undefined;

    window.clearTimeout(autoSelectedAddressToastTimerRef.current);
    autoSelectedAddressToastTimerRef.current = window.setTimeout(
      () => {
        setAutoSelectedAddressToast('');
        autoSelectedAddressIdRef.current = '';
      },
      Number(
        get(
          resources,
          'autoSelectedAddressToastDurationInMS',
          resourcesConfig.autoSelectedAddressToastDurationInMS,
        ),
      ) || toastMessageTime,
    );

    return () => window.clearTimeout(autoSelectedAddressToastTimerRef.current);
  }, [canShowAutoSelectedAddressToast, autoSelectedAddressToastSequence, resources]);

  useEffect(() => {
    if (isNativeWebView) {
      window.addEventListener('NewLocationSet', handleLocationEventFromNative);
    }

    return () => {
      if (isNativeWebView) {
        window.removeEventListener('NewLocationSet', handleLocationEventFromNative);
      }
    };
  }, []);

  const handleLocationEventFromNative = () => {
    const draftLocation = getItem(storageTypes.localStorage, 'geoLocation');
    const draftStatus = getItem(storageTypes.localStorage, 'geoLocationPermissionStatus');
    setGuestLocation(draftLocation);
    setLocationPermissionStatus(draftStatus);
  };

  const handleDeliveryAddressCTA = () => {
    setShowDeliveryAddressPopup(false);
    const deliveryPath = `/${bookingRoutes.containers.delivery}`;
    if (!isEqual(get(location, 'pathname', ''), deliveryPath)) {
      history.push(deliveryPath);
    }
  };

  useEffect(() => {
    setItem(storageTypes.sessionStorage, 'isLocationAnimationRendered', true);
    setItem(storageTypes.sessionStorage, 'isRewardsGreeterRendered', true);
    const guestLoginData = getItem(storageTypes.localStorage, 'GuestLoginData');
    const guestId = get(guestLoginData, 'id', '');
    const subDomain = extractWhitelabelSubdomain();
    const { pathname, search } = location;
    let callbackUrl = search ? `${pathname}${search}` : pathname;
    callbackUrlRef.current = callbackUrl;
    const activePathName = getActiveContainerRoute(pathname);
    if (subDomain) {
      const appStartupReqBody = {
        subdomain: subDomain,
        includeModules: ['reward', 'brand', 'resource'],
      };
      if (isNativeWebView) {
        const startupInfoData = {
          eventType: { type: nativeEventType.appStartupInfo },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(startupInfoData));
        // fetch Startup Info
        // fetchStartupInfoData({ appStartupReqBody });
        // event listner for ios
        let dataFromApp = '';
        window.addEventListener('message', function (msg) {
          dataFromApp = get(msg, 'data', '');
          if (!isEmpty(dataFromApp)) {
            dataFromApp = dataFromApp && JSON.parse(dataFromApp);
            const { eventType, data } = dataFromApp;

            if (isEqual(eventType, nativeEventType.appStartupInfo) && !isEmpty(data)) {
              // dispatch app startup data into redux
              actions.dispatchStartupInfoAndResourceData(data);
            } else {
              // fetch Startup Info
              // fetchStartupInfoData({ appStartupReqBody });
            }
            if (isEqual(eventType, nativeEventType.sessionHandled)) {
              setIsApiRequestOnHold(false);
            }
          } else {
            // fetch Startup Info
            // fetchStartupInfoData({ appStartupReqBody });
          }
        });

        // event listner for android
        document.addEventListener('message', function (msg) {
          dataFromApp = get(msg, 'data', '');

          if (!isEmpty(dataFromApp)) {
            dataFromApp = dataFromApp && JSON.parse(dataFromApp);
            const { eventType, data } = dataFromApp;
            if (isEqual(eventType, nativeEventType.appStartupInfo) && !isEmpty(data)) {
              // dispatch app startup data into redux
              actions.dispatchStartupInfoAndResourceData(data);
              setStartupInteractionDone(true);
            } else {
              // fetch Startup Info
              // fetchStartupInfoData({ appStartupReqBody });
            }
            if (isEqual(eventType, nativeEventType.sessionHandled)) {
              setIsApiRequestOnHold(false);
            }
          } else {
            // fetch Startup Info
            // fetchStartupInfoData({ appStartupReqBody });
          }
        });
      }

      if (isNativeWebView) {
        // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there
        const nativeAppUpdateAvailable = {
          eventType: { type: nativeEventType.nativeAppUpdateAvailable },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(nativeAppUpdateAvailable));
        const appVersionData = {
          eventType: { type: nativeEventType.appVersion },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(appVersionData));

        // add event listeners for catch the event from web view
        // event listner for ios
        window.addEventListener('message', function (message) {
          extractDataFromNative({
            message,
            history,
            actions,
            handleNativeBackCTA,
            handleLogout,
            handleNativeRouteName,
          });
        });
        // event listner for android
        document.addEventListener('message', function (message) {
          extractDataFromNative({
            message,
            history,
            actions,
            handleNativeBackCTA,
            handleLogout,
            handleNativeRouteName,
          });
        });
      }
    } else {
      history.push(callbackUrl);
    }

    // ignore greeter for get app
    const greeterExceptionalRoutes = [
      bookingRoutes.containers.getApp,
      bookingRoutes.containers.scan,
      bookingRoutes.containers.delivery,
    ];

    if (greeterExceptionalRoutes.includes(activePathName)) {
      setGreetingSplashActive(false);
    }

    // component un mount logic
    return () => {
      actions.dispatchContextualHome('');
    };
  }, []);

  useEffect(() => {
    const storedUserDetails = getItem(storageTypes.localStorage, 'GuestLoginData') || {};
    const storedAuthData = getItem(storageTypes.localStorage, 'AuthData') || {};
    const userId = get(userDetails, 'id', get(storedUserDetails, 'id', ''));
    const currentAccessToken = get(authData, 'accessToken', get(storedAuthData, 'accessToken', ''));
    const { pathname, search } = location;
    let callbackUrl = callbackUrlRef.current
      ? callbackUrlRef.current
      : search
      ? `${pathname}${search}`
      : pathname;
    const activePathName = getActiveContainerRoute(callbackUrl);
    if (!authenticationExemptedRoutes.includes(activePathName)) {
      if (isEmpty(currentAccessToken)) {
        setShowLocationAnimation(false);
        actions.dispatchCallbackUrl({ callbackUrl }).then(() => {
          history.replace(
            `/${bookingRoutes.containers.authentication}/${bookingRoutes.authentication.signUp}${
              search ? search : ''
            }`,
          );
        });
      } else {
        actions.fetchUserDetails({ userId, accessToken: currentAccessToken }).then((response) => {
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
            // A valid access token is the authentication source of truth. Profile hydration
            // is enrichment and must never bounce a verified user back to sign-in.
            if (callbackUrl) {
              history.replace(callbackUrl);
            }
          }
        });
      }
    }
  }, []);

  useEffect(() => {
    const isHomeRoute = pathname.includes(bookingRoutes.containers.home);
    if (!isHomeRoute) {
      // Saved addresses are only needed on Home. Delivery/signup flows should
      // proceed directly to reverse geocoding after native permission grants.
      setAddressFetching(false);
      return;
    }

    const storedUserDetails = getItem(storageTypes.localStorage, 'GuestLoginData') || {};
    const userId = get(userDetails, 'id', '') || get(storedUserDetails, 'id', '');
    if (isEmpty(userId)) {
      setAddressFetching(false);
      return;
    }

    // The saved profile can hydrate after Home on a native cold launch. The
    // persisted login id lets us load addresses immediately, so a nearby Home
    // or Work address is selected before the raw reverse-geocoded location.
    setAddressFetching(true);
    actions
      .getUserAddresses({ userId })
      .then(() => setAddressFetching(false))
      .catch(() => {
        // A failed saved-address lookup must not block native-coordinate
        // reverse geocoding or leave the location animation spinning.
        setAddressFetching(false);
      });
  }, [get(userDetails, 'id'), pathname]);

  useEffect(() => {
    if (isNativeWebView) {
      const activePathName = getActiveContainerRoute(pathname);
      if (!isEqual(activePathName, bookingRoutes.containers.authentication)) {
        const nativeAppUpdateAvailable = getItem(
          storageTypes.sessionStorage,
          'nativeAppUpdateAvailable',
        );
        if (!nativeAppUpdateAvailable) {
          const appVersion = getItem(storageTypes.sessionStorage, 'appVersion');
          const appUpdateConfigObj =
            appResources &&
            appResources.appUpdate &&
            appResources.appUpdate.find((appConfig) => appConfig.platform === deviceType);
          const minReqAppVersion = get(appUpdateConfigObj, 'minRequiredAppVersion');
          const defaultAppVersion = get(appUpdateConfigObj, 'currentAppVersion');
          const webFooterApplicableVersion = get(appUpdateConfigObj, 'webFooterApplicableVersion');
          const currentAppVersion = appVersion ? appVersion : defaultAppVersion;

          if (!isEmpty(currentAppVersion)) {
            if (
              webFooterApplicableVersion &&
              compareVersions(currentAppVersion, webFooterApplicableVersion) < 0
            ) {
              // Show footer of web until the user didn't install latest version
              if (!isIosWebView) {
                setShowShareAppFeature(false);
              }
            }
          }
        }
      }
    }
  }, [pathname]);

  useEffect(() => {
    if (
      (!greetingSplashActive && showGreeterScreen) ||
      !showGreeterScreen ||
      !isEmpty(pagesExistInNative)
    ) {
      if (isNativeWebView) {
        // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there

        const data = {
          eventType: { type: 'greeterScreenRendered' },
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(data));
      }
    }
  }, [greetingSplashActive, showGreeterScreen]);

  useDidUpdateEffect(() => {
    setAppResources(resources);
  }, [resources]);

  useEffect(() => {
    if (isNativeWebView) {
      // ensure window.ReactNativeWebView is there, otherwise, web app might crash if is not there
      const data = {
        eventType: { type: 'routeName', data: location },
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(data));
    }
  }, [location]);

  useEffect(() => {
    const defaultFontURL = DefaultRegularFont;
    const defaultFont = 'DefaultFont';
    const fontURL = !isEmpty(fontFamilyUrl) ? fontFamilyUrl : DefaultRegularFont;
    const RegularFont = 'RegularFont';
    const bFontURL = !isEmpty(boldFontURL) ? boldFontURL : DefaultBoldFont;
    const BoldFont = 'BoldFont';
    const sBFontURL = !isEmpty(semiBoldFontURL) ? semiBoldFontURL : DefaultSemiBoldFont;
    const SemiBoldFont = 'SemiBoldFont';
    var newStyle = document.createElement('style');
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
    font-family: ' +
          RegularFont +
          ";\
    src: url('" +
          fontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          BoldFont +
          ";\
  src: url('" +
          bFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          SemiBoldFont +
          ";\
  src: url('" +
          sBFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          defaultFont +
          ";\
  src: url('" +
          defaultFontURL +
          "') format('woff');\
}\
",
      ),
    );
    document.head.appendChild(newStyle);
  }, [fontFamilyUrl, boldFontURL, semiBoldFontURL]);

  useDidUpdateEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }); // eslint-disable-line
    if (isNativeWebView && !isIosWebView) {
      // handling for android devices scroll(as above event stops scrolls in middle due to native webview event handling)
      setTimeout(() => {
        if (window.pageYOffset > 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      }, 200);
    }
  }, [pathname]);

  useEffect(() => {
    if (
      !isEmpty(errorResponse) &&
      ![
        'PASS_SUBSCRIBE_ERROR',
        'OTP_INVALID',
        'CART_SUBCATEGORY_MISMATCH',
        'SLOT_START_IN_PAST',
        'CART_EMPTY',
        serverErrorCodes.invalidToken,
      ].includes(get(errorResponse, 'code'))
    ) {
      const { code, message } = errorResponse;
      const draftErrorContent = {
        title: getErrorTitle({ source: resourcesConfig, code }),
        description: getErrorDescription({
          source: resourcesConfig,
          code,
          message,
        }),
      };
      setShowErrorPopup(true);
      setErrorContent(draftErrorContent);
    }
    if (
      !isEmpty(errorResponse) &&
      isEmpty(accessToken) &&
      isEqual(get(errorResponse, 'code'), serverErrorCodes.invalidToken)
    ) {
      handleSessionExpiredErrorResponse();
    }
  }, [errorResponse, accessToken]);

  const handleLogout = (props) => {
    const prefilledData = get(props, 'prefilledData', {});
    localStorage.setItem('GuestLoginData', JSON.stringify({}));
    localStorage.setItem('AuthData', JSON.stringify({}));
    actions.dispatchCallbackUrl({ callbackUrl: '' });
    actions.dispatchLogout();
    history.replace(
      `/${bookingRoutes.containers.authentication}/${
        !isEmpty(prefilledData)
          ? bookingRoutes.authentication.signIn
          : bookingRoutes.authentication.signUp
      }`,
      { prefilledData },
    );
  };

  const handleSessionExpiredErrorResponse = () => {
    if (isNativeWebView) {
      // send session expired event to native to let native handle the session expired
      const sessionExpiredData = {
        eventType: { type: nativeEventType.sessionExpired },
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(sessionExpiredData));
      return;
    }
    if (sessionExpiryLogoutTimerRef.current) return;
    setIsApiRequestOnHold(false);
    setShowSessionExpired(true);
    sessionExpiryLogoutTimerRef.current = window.setTimeout(() => {
      sessionExpiryLogoutTimerRef.current = undefined;
      handleCloseSessionExpired();
    }, Number(resourcesConfig.sessionExpiryLogoutDelayInMS));
  };

  const handleCloseSessionExpired = () => {
    window.clearTimeout(sessionExpiryLogoutTimerRef.current);
    sessionExpiryLogoutTimerRef.current = undefined;
    setShowSessionExpired(false);
    actions.clearApiErrorResponse();
    const prefilledData = {
      primaryPhoneNumber: get(
        userDetails,
        'mobileNumber',
        get(userDetails, 'primaryMobileNumber', ''),
      ),
      errorResponse,
      isSessionExpired: true,
    };
    handleLogout({ prefilledData });
  };

  const handleNativeRouteName = (routeName) => {
    setNativeRouteName(routeName);
  };

  // Function to fetch locality details using reverse geocoding
  const fetchLocalityDetails = async (latitude, longitude) => {
    const parsedLatitude = Number(latitude);
    const parsedLongitude = Number(longitude);
    if (!Number.isFinite(parsedLatitude) || !Number.isFinite(parsedLongitude)) {
      console.warn('Skipping locality lookup: invalid coordinates', { latitude, longitude });
      return;
    }
    setFetchingLocality(true);
    try {
      const response = await fetchGoogleMapsApi({
        latitude: parsedLatitude,
        longitude: parsedLongitude,
      });
      if (get(response, 'data.results.length', 0) > 0) {
        // A saved address may have been auto-selected while reverse geocoding
        // was in flight. Never let this older, ID-less result replace it.
        if (
          get(selectedServiceAddressRef.current, 'id') ||
          get(selectedServiceAddressRef.current, 'currentLocation', false)
        ) {
          return;
        }
        const addressInfo = get(response, 'data.results.0', {});
        const addressComponents = get(addressInfo, 'address_components', []);
        const formattedAddress = getLocalityAddressFromAddressComponent(addressComponents);
        const { locality, subLocality, administrativeArea } = formattedAddress || {};

        const addressName = subLocality;
        const address = `${locality ? locality + ', ' : ''}${administrativeArea || ''}`;

        actions.dispatchGuestLocation({
          guestLocation: { latitude: parsedLatitude, longitude: parsedLongitude, addressName, address },
        });

        setLocalityDetails(formattedAddress);
      }
    } catch (error) {
      console.error('Error fetching locality details:', error);
    } finally {
      setFetchingLocality(false);
      setFetchingLocation(false);
    }
  };

  const fetchStartupInfoData = ({ appStartupReqBody }) =>
    actions.fetchAppStartupInfo({ appStartupReqBody }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
        actions.dispatchContextualHome(`/${bookingRoutes.containers.home}`);
        setStartupInteractionDone(true);
      } else if (!isEqual(get(response, 'data.code'), serverErrorCodes.invalidToken)) {
        // incase if sub domain is not resolved
        setShowNonExistentSubdomainFallback(true);
      }
    });

  const getActiveTabContext = (pathname) => {
    let currentActiveTabContext = activeTabContext;
    const paths = pathname.trim().split('/').filter(Boolean); // removes empty strings

    if (paths.length > 0) {
      currentActiveTabContext = paths[0]; // first part of the path e.g. "account"
    }

    return currentActiveTabContext;
  };
  const onCloseExceptionComponent = () => {
    actions.clearApiErrorResponse().then(() => {
      setShowErrorPopup(false);
      setErrorContent({});
    });
  };

  const getActiveRoute = (pathname) => {
    if (pathname) {
      const paths = pathname.trim().split('/') || [];
      let route = '';
      route = paths.slice(1, paths.length).join('/');
      return route;
    }
  };

  const activeRoute = getActiveRoute(get(location, 'pathname', ''));

  const handleNavTabClick = (type) => {
    switch (type) {
      case bookingRoutes.containers.home:
        return history.push(`/${bookingRoutes.containers.home}`);
      case bookingRoutes.containers.locations:
        return history.push(`/${bookingRoutes.containers.locations}`);
      case bookingRoutes.containers.account:
        return history.push(`/${bookingRoutes.containers.account}`);
      case bookingRoutes.containers.bookings:
        return history.push(`/${bookingRoutes.containers.bookings}`);
      default:
        return;
    }
  };

  const handleNonExistentSubdomainClick = () => {
    history.push(`/${bookingRoutes.containers.home}`);
    window.location.reload();
  };

  // Handle native back button
  const handleNativeBackCTA = (key) => {
    const backButtonElement = document.getElementById(key);
    if (backButtonElement !== null) {
      backButtonElement.click();
    } else {
      const entryRoutes = [bookingRoutes.authentication, bookingRoutes.containers.home];
      const pathname = get(location, 'pathname');
      if (!entryRoutes.some((entryRoute) => pathname.includes(entryRoute))) {
        history.push(`/${bookingRoutes.containers.home}`);
      }
    }
  };

  const userName = get(userDetails, 'name', '');
  const showGreeterText = get(appResources, 'showGreeterText', true);
  const greeterVisibilityTimeInMS = get(appResources, 'greeterVisibilityTimeInMS', 3000);
  const greeterStaticInfo = get(appResources, 'staticPages', []).find((page) =>
    isEqual(page.route, 'greeter'),
  );
  const greeterHTML = get(greeterStaticInfo, 'content', '');

  const LocationChangeCallback = withRouter(({ location: routerLocation }) => {
    // sync location state with React Router's location from Route context
    useEffect(() => {
      setLocation(routerLocation);
    }, [routerLocation]);

    return null;
  });

  const renderRoutes = () => (
    <>
      {showNonExistentSubdomainFallback ? (
        <NonExistentSubdomainFallback
          resources={{
            title: (
              <FormattedMessage
                id="nonExistSubdomainFallbackTitle"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackTitle}
              />
            ),
            description: (
              <FormattedMessage
                id="nonExistSubdomainFallbackDescription"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackDescription}
              />
            ),
            atnBtn: (
              <FormattedMessage
                id="nonExistSubdomainFallbackCTA"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackCTA}
              />
            ),
          }}
          handleGoHome={() => handleNonExistentSubdomainClick()}
        />
      ) : (
        <Router history={history}>
          <React.Fragment>
            {/* location change callback for updating the location state in the container*/}
            <LocationChangeCallback />
            <Switch location={location} key={location.pathname}>
              <Route
                exact
                path="/"
                render={() =>
                  location.pathname === '/' ? (
                    <Redirect to={`/${bookingRoutes.containers.home}`} />
                  ) : null
                }
              />
              <Route
                path={`/${bookingRoutes.containers.authentication}`}
                component={Authentication}
              />
              <Route path={`/${bookingRoutes.containers.home}`} component={Home} />
              <Route
                path={`/${bookingRoutes.containers.account}`}
                render={(props) => <Account showShareAppFeature={showShareAppFeature} {...props} />}
              />
              <Route path={`/${bookingRoutes.containers.getApp}`} component={GetApp} />
              <Route path={`/${bookingRoutes.containers.rewardList}`} component={RewardList} />
              <Route path={`/${bookingRoutes.containers.coupons}`} component={CouponsList} />
              <Route
                path={`/${bookingRoutes.containers.rewardProgramDetails}`}
                component={RewardProgramDetails}
              />
              <Route path={`/${bookingRoutes.containers.referral}`} component={Referral} />
              <Route exact path={`/${bookingRoutes.containers.locations}`} component={Locations} />
              <Route exact path={`/${bookingRoutes.containers.location}`} component={Location} />
              <Route
                exact
                path={`/${bookingRoutes.containers.selectOutlet}/:brandId`}
                component={SelectOutlet}
              />
              <Route
                path={`/${bookingRoutes.containers.locations}/:outletId`}
                component={LocationDetails}
              />
              <Route path={`/${bookingRoutes.containers.aboutUs}`} component={AboutUs} />
              <Route
                path={`/${bookingRoutes.containers.deleteAccountSuccess}`}
                component={DeleteAccountSuccess}
              />
              <Route path={`/${bookingRoutes.containers.helpSupport}`} component={HelpAndSupport} />
              <Route path={`/${bookingRoutes.containers.policies}`} component={Policies} />
              <Route path={`/${bookingRoutes.containers.vipPassList}`} component={VIPPassList} />
              <Route
                path={`/${bookingRoutes.containers.vipPassDetail}/:passCode`}
                component={VIPPassDetails}
              />
              <Route
                path={`/${bookingRoutes.containers.vipMembershipList}`}
                component={VIPMembershipList}
              />
              <Route
                path={`/${bookingRoutes.containers.bookService}/:categorySlug/:subcategorySlug`}
                component={BookService}
              />
              <Route
                path={`/${bookingRoutes.containers.bookService}/:subcategorySlug`}
                component={BookService}
              />
              <Route path={`/${bookingRoutes.containers.bookings}`} component={Bookings} />
              <Route
                exact
                path={`/${bookingRoutes.containers.bookingFeedback}/:bookingId`}
                component={BookingFeedback}
              />
              <Route
                exact
                path={`/${bookingRoutes.containers.confirmation}/order/:bookingOrderId/booking/:bookingId`}
                component={Confirmation}
              />
              <Route
                exact
                path={`/${bookingRoutes.containers.confirmation}/order/:bookingOrderId`}
                component={Confirmation}
              />
              <Route
                exact
                path={`/${bookingRoutes.containers.confirmation}/:bookingId`}
                component={Confirmation}
              />
              <Route path={`/${bookingRoutes.containers.cart}`} component={Cart} />
              <Route exact path={`/${bookingRoutes.containers.payment}`} component={Payment} />
              <Route
                path={`/${bookingRoutes.containers.delivery}`}
                render={(props) => <Delivery key={props.location.search} {...props} />}
              />
              <Route
                path={`/${bookingRoutes.containers.categoryCatalog}/:categorySlug`}
                component={CategoryCatalog}
              />
              <Route
                path={`/${bookingRoutes.containers.serviceCatalog}/:categorySlug/:subcategorySlug`}
                render={(props) => <ServiceCatalog key={props.location.search} {...props} />}
              />
              <Route
                path={`/${bookingRoutes.containers.serviceCatalog}/:subcategorySlug`}
                render={(props) => <ServiceCatalog key={props.location.search} {...props} />}
              />
            </Switch>
          </React.Fragment>
        </Router>
      )}
    </>
  );

  return (
    <div
      className={classNames('rewards', {
        'in-app-style': isIosWebView || useNativeWebInset,
        // Android WebView renders edge-to-edge; route-level spacing is scoped
        // to this marker so browser and iOS layouts retain their existing CSS.
        'android-webview': isNativeWebView && isAndroidWebView,
      })}
      id="rewards-container"
    >
      {/* render greeting splash screen here */}
      <ThemeContext.Provider
        value={{
          themeContext: bookingRoutes.booking,
          nativeRouteName: nativeRouteName,
          guestLocation,
          fetchingLocation,
          locationPermissionStatus,
          showDeliveryAddressPopup,
          setShowDeliveryAddressPopup,
          findingNearbyCompanion,
          setFindingNearbyCompanion,
          autoSelectedAddressToast,
          autoSelectedAddressToastSequence,
          canShowAutoSelectedAddressToast,
        }}
      >
        <IntlProvider messages={resources} locale="en-US" defaultLocale="en-US">
          {greetingSplashActive &&
            isThemeApplied &&
            showGreeterScreen &&
            !isEmpty(appResources) &&
            isEmpty(pagesExistInNative) && (
              <RewardsGreeting
                active={greetingSplashActive}
                deactivate={() => setGreetingSplashActive(false)}
                splashBgImage={get(appResources, 'greeterSplashScreen', '')}
                loggedInGuestName={userName}
                showTimeSalutation
                showGreeterText={showGreeterText}
                splashDuration={greeterVisibilityTimeInMS}
                brandSprite={get(appResources, 'brandSprite', '')}
                showStaticGreeter={get(appResources, 'showStaticGreeter', false)}
                greeterHTML={greeterHTML}
              />
            )}
          {isLocationPermissionRoute &&
            shouldShowLocationPermissionCard &&
            (showLocationAnimation ||
              (!locationPermissionPromptDismissed && locationAccessUnavailable)) && (
              <LocationPickerAnimation
                setShowLocationAnimation={setShowLocationAnimation}
                setLocationPermissionPromptDismissed={setLocationPermissionPromptDismissed}
              />
            )}
          {isThemeApplied && (
            <>
              {!isEmpty(pagesExistInNative) ? (
                renderRoutes()
              ) : (
                <SwitchTransition component={null}>
                  <CSSTransition key={location.pathname} timeout={300} classNames={transitionType}>
                    {renderRoutes()}
                  </CSSTransition>
                </SwitchTransition>
              )}
              {matchRoutePatternWithWildcardSupport(navigationalPanelRoutes, activeRoute) && (
                <NavigationPanel
                  onNavTabClick={(type) => {
                    handleNavTabClick(type);
                  }}
                  activeTabContext={getActiveTabContext(get(location, 'pathname'))}
                />
              )}
              {showErrorPopup && (
                <ExceptionComponent
                  pageType="HalfSize"
                  openSegmentPopup={showErrorPopup}
                  dismissSegmentPopup={onCloseExceptionComponent}
                  title={get(errorContent, 'title')}
                  description={get(errorContent, 'description')}
                />
              )}
              {showSessionExpired && <SessionExpiredPopover open={showSessionExpired} />}
              {showDeliveryAddressPopup && (
                <DeliveryAddressPopup
                  openSegmentPopup={showDeliveryAddressPopup}
                  dismissSegmentPopup={() => setShowDeliveryAddressPopup(false)}
                  showSelfPickUpSection={false}
                  handleDeliveryAddressCTA={handleDeliveryAddressCTA}
                  showAddNewAddress={false}
                  deviceLocation={guestLocation}
                />
              )}
            </>
          )}
        </IntlProvider>
      </ThemeContext.Provider>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appStartupInfo: get(state, 'common.appStartupInfo', {}),
  contextualHome: get(state, 'common.contextualHome', ''),
  errorResponse: get(state, 'common.errorResponse', {}),
  resources: get(state, 'common.resources', {}),
  themeData: get(state, 'common.resources.themeData', {}),
  fontFamilyUrl: get(state, 'common.resources.themeData.fontFamilyUrl', ''),
  semiBoldFontURL: get(state, 'common.resources.themeData.semiBoldFontURL', ''),
  boldFontURL: get(state, 'common.resources.themeData.boldFontURL', ''),
  outletDetails: get(state, 'common.outletDetails', {}),
  userDetails: get(state, 'authentication.userDetails', {}),
  authData: get(state, 'authentication.authData', {}),
  userAddresses: get(state, 'common.userAddresses', []),
  selectedServiceAddress: get(state, 'common.guestLocation', {}),
  hasTiers: get(state, 'common.appStartupInfo.rewardProgram.hasTiers', false),
  defaultWebRedirectionUrl: get(state, 'common.resources.defaultWebRedirectionUrl', ''),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppStartupInfo: fetchAppStartupInfoAction,
      clearApiErrorResponse: clearApiErrorResponseAction,
      fetchUserDetails: fetchUserDetailsAction,
      getUserAddresses: getUserAddressesAction,
      dispatchContextualHome,
      dispatchCallbackUrl: dispatchCallbackUrlAction,
      dispatchStartupInfoAndResourceData: dispatchStartupInfoAndResourceDataAction,
      dispatchLogout: dispatchLogoutAction,
      dispatchGuestLocation: dispatchGuestLocationAction,
      dispatchAuthToken: dispatchAuthTokenAction,
      fetchAppResources: fetchAppResourcesAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
